import type React from 'react';

import type * as api from '@gitbook/api-client';
import {
    createPalette,
    PaletteButton,
    type PaletteCommandDef,
    PaletteCommandType,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    CardBody,
    CardCheck,
    CardRadio,
    Description,
    Heading,
    type IconDefinition,
    useRadioGroupContext,
} from '@gitbook/spine-react';
import { executeSuspenseAll } from '@gitbook/util-suspense-cache';
import { assertNever } from '@gitbook/util-ts';

import {
    assertIsExtendedSiteInstallation,
    assertIsExtendedSpaceInstallation,
    flattenSpacesInOrgAllContent,
    getSiteIcon,
    SpaceIcon,
} from '../../../../../Components';
import { apiResource, readAPICache } from '../../../../../state';

/**
 * Radio card for selecting which sites/spaces to install an integration on.
 */
export function SelectContentCard(props: {
    value: 'site_selected' | 'space_selected';
    integration: api.Integration;
    installation: api.IntegrationInstallation;
    organizationId: string;
    selectedContentIds: string[];
    setSelectedContentIds: (contentIds: string[]) => void;
}) {
    const {
        value,
        integration,
        installation,
        organizationId,
        selectedContentIds,
        setSelectedContentIds,
    } = props;

    const radioContext = useRadioGroupContext();
    const selected = radioContext.selectedValue === value;

    const type = value === 'site_selected' ? 'site' : 'space';

    const palette = createPaletteFactory({
        label: `Find ${type}s…`,
        selectedIds: selectedContentIds,
        onChange: (selectedIds) => {
            setSelectedContentIds(selectedIds);
        },

        getContentSuspense: () => {
            const allContent = readAPICache(
                apiResource('getOrganizationAllContent', { organizationId })
            );

            if (type === 'site') {
                return allContent.sites.map(({ site }) => ({
                    id: site.id,
                    title: site.title,
                    IconC: getSiteIcon(site),
                }));
            }

            if (type === 'space') {
                return flattenSpacesInOrgAllContent(allContent, ({ space }) => ({
                    id: space.id,
                    title: space.title,
                    IconC: () => <SpaceIcon space={space} />,
                }));
            }

            assertNever(type);
        },

        getInstalledContentIdsSuspense: () => {
            if (type === 'site') {
                const installations = readAPICache(
                    apiResource(
                        'listIntegrationInstallationSites',
                        {
                            integrationName: integration.name,
                            installationId: installation.id,
                            extended: true,
                        },
                        { nullOnError: true }
                    )
                );

                return (
                    installations?.items.map((installation) => {
                        assertIsExtendedSiteInstallation(installation);
                        return installation.site.id;
                    }) ?? []
                );
            }

            if (type === 'space') {
                const installations = readAPICache(
                    apiResource(
                        'listIntegrationInstallationSpaces',
                        {
                            integrationName: integration.name,
                            installationId: installation.id,
                            extended: true,
                        },
                        { nullOnError: true }
                    )
                );

                return (
                    installations?.items.map((installation) => {
                        assertIsExtendedSpaceInstallation(installation);
                        return installation.space.id;
                    }) ?? []
                );
            }

            assertNever(type);
        },
    });

    return (
        <CardRadio value={value} className="flex flex-col">
            <CardBody>
                <div className="flex size-full items-center justify-between rounded-md">
                    <div className="flex gap-3">
                        {type === 'space' ? (
                            <Icon.Space className="size-4.5 text-muted" />
                        ) : (
                            <Icon.DocsSite className="size-4.5 text-muted" />
                        )}
                        <div className="flex flex-col items-start gap-3">
                            <div>
                                <Heading>{`Install on specific ${type}(s)`}</Heading>
                                <Description>
                                    {`Available only to chosen ${type}s once configured.`}
                                </Description>
                            </div>

                            {selected ? (
                                <PaletteButton
                                    label={
                                        selectedContentIds.length === 0
                                            ? `Choose ${type}s`
                                            : `${selectedContentIds.length} ${type}(s) selected`
                                    }
                                    trailingIcon={Icon.ChevronDown}
                                    kind="secondary"
                                    palette={palette}
                                />
                            ) : null}
                        </div>
                    </div>

                    <CardCheck className="ml-auto" />
                </div>
            </CardBody>
        </CardRadio>
    );
}

/**
 * Create a factory function for creating the palette. Handles sites and spaces, as well as any suspense.
 */
function createPaletteFactory<
    Content extends { id: string; title: string; IconC: IconDefinition | React.ReactNode },
>(props: {
    label: string;

    selectedIds: string[];
    onChange: (selectedIds: string[]) => void;

    /** Return the list of content (sites or spaces) that should be shown in the palette */
    getContentSuspense: () => readonly Content[];

    /** Return a list of ids that have already been installed. */
    getInstalledContentIdsSuspense: () => readonly string[];
}) {
    const { selectedIds, label, onChange, getContentSuspense, getInstalledContentIdsSuspense } =
        props;

    function createCommands(
        contents: readonly Content[],
        installedContentIds: readonly string[],
        pendingInstallations: string[]
    ): PaletteCommandDef[] {
        return contents.map((content) => {
            const itemId = content.id;
            const isInstalled = installedContentIds.includes(content.id);
            const isSelected = isInstalled || pendingInstallations.includes(content.id);

            return {
                key: itemId,
                selected: isSelected,
                title: content.title,
                disabled: isInstalled,
                subtitle: isInstalled ? 'Already installed' : undefined,
                icon: content.IconC,
                trigger: (palette) => {
                    const updated = toggleItemInArray(pendingInstallations, content.id);
                    onChange(updated);
                    return {
                        ...palette,
                        groups: palette.groups.map((group) => ({
                            ...group,
                            commands: createCommands(contents, installedContentIds, updated),
                        })),
                    };
                },
                type: PaletteCommandType.CHECKBOX,
            };
        });
    }

    return () => {
        return createPalette({
            search: { placeholder: label },
            position: { width: PalettePositionWidth.LARGE },
            groups: [
                {
                    key: label,
                    commands: () => {
                        const [contents, installedContentIds] = executeSuspenseAll([
                            getContentSuspense,
                            getInstalledContentIdsSuspense,
                        ] as const);

                        return createCommands(contents, installedContentIds, selectedIds);
                    },
                },
            ],
        });
    };
}

function toggleItemInArray(arr: string[], item: string) {
    return arr.includes(item) ? arr.filter((id) => id !== item) : arr.concat(item);
}
